import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { GiftCategory, GiftKlydo, KlydoGiftProps } from '../../../api/gift';
import useGiftCategories from '../hooks/useGiftCategories';
import axios from 'axios';
import { getDeviceAppropriateAnimationUrl } from '../utils';
import { useHistory } from '../hooks/useHistory';
import steps from '../Steps/steps';
import firebaseService from '../../../firebase/firebaseService';

export type GiftContextType = {
  selectedOccasion?: GiftCategory;
  selectedKlydoGreet?: GiftKlydo;
  customKlydoId?: string;
  klydoGiftProps: KlydoGiftProps;
  next: () => void;
  back?: () => void;
  loading?: boolean;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedOccasion: React.Dispatch<
    React.SetStateAction<GiftCategory | undefined>
  >;
  setSelectedKlydoGreet: React.Dispatch<
    React.SetStateAction<GiftKlydo | undefined>
  >;
  setCustomKlydoId: React.Dispatch<React.SetStateAction<string | undefined>>;
  setKlydoGiftProps: (props: Partial<KlydoGiftProps>) => void;
  startNewGift: (props: Partial<KlydoGiftProps>) => void;
  resetSenderImageUrl: () => void;
  giftCategories: GiftCategory[] | undefined;
  admin: boolean;
};

const GiftContext = createContext<GiftContextType>({} as GiftContextType);

export const useGiftContext = () => useContext(GiftContext);

export const GiftContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const defaultPhoto =
    'https://res.cloudinary.com/klydoclock/image/upload/profile/defaultPhoto_mc2iv6.png';

  const { data: giftCategories, isLoading } = useGiftCategories();
  const { setQueryParam, search, goBack } = useHistory();

  const [initializing, setInitializing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [admin, setAdmin] = useState(false);
  const [selectedOccasion, setSelectedOccasion] = useState<GiftCategory>();
  const [selectedKlydoGreet, setSelectedKlydoGreet] = useState<GiftKlydo>();
  const [customKlydoId, setCustomKlydoId] = useState<string | undefined>();
  const emptyGiftProps: KlydoGiftProps = {
    senderImageUrl: defaultPhoto,
    senderName: '',
    greetingContent: '',
    giftId: '',
    orderName: '',
    clockId: '',
    klydoId: '',
  };
  const [klydoGiftProps, setKlydoGiftPropsState] =
    useState<KlydoGiftProps>(emptyGiftProps);

  const setKlydoGiftProps = (props: Partial<KlydoGiftProps>) => {
    setKlydoGiftPropsState((prev) => ({ ...prev, ...props }));
  };

  // One provider serves every /gift route, so a gift opened earlier in the
  // same tab stays in state. A new gift starts from empty, not from it.
  const startNewGift = (props: Partial<KlydoGiftProps>) => {
    setKlydoGiftPropsState({ ...emptyGiftProps, ...props });
    setSelectedOccasion(undefined);
    setSelectedKlydoGreet(undefined);
    setCustomKlydoId(undefined);
  };

  const resetSenderImageUrl = () => {
    setKlydoGiftPropsState((prev) => ({
      ...prev,
      senderImageUrl: defaultPhoto,
    }));
  };

  // Check admin role using onAuthStateChanged so we wait for Firebase to
  // restore auth state — currentUser is null until then on page load.
  useEffect(() => {
    const unsubscribe = firebaseService.onAuthStateChanged((user) => {
      if (!user) return;
      user.getIdTokenResult().then((result) => {
        if (result.claims?.role === 'admin') setAdmin(true);
      });
    });
    return () => unsubscribe();
  }, []);

  const prefetchController = useRef(new AbortController());

  const fetchAll = async () => {
    if (!initializing) return;
    prefetchController.current.abort();
    prefetchController.current = new AbortController();
    try {
      await Promise.all(
        (giftCategories ?? []).map(async (category) => {
          await Promise.all(
            category.klydos.slice(0, 5).map(async (klydo) => {
              try {
                const animationUrl = getDeviceAppropriateAnimationUrl(klydo);
                await axios.get(animationUrl, {
                  signal: prefetchController.current.signal,
                });
              } catch {
                // ignore prefetch errors
              }
            }),
          );
        }),
      );
    } finally {
      setInitializing(false);
    }
  };

  useEffect(() => {
    if (selectedOccasion) {
      prefetchController.current.abort();
    }
  }, [selectedOccasion]);

  useEffect(() => {
    if (klydoGiftProps?.senderImageUrl) {
      axios.get(klydoGiftProps.senderImageUrl).catch(console.error);
    }
  }, [klydoGiftProps?.senderImageUrl]);

  useEffect(() => {
    if (!isLoading && (giftCategories?.length ?? 0) > 0) {
      fetchAll();
    }
  }, [isLoading, giftCategories]);

  useEffect(() => {
    if (
      klydoGiftProps?.klydoId &&
      (giftCategories?.length ?? 0) > 0 &&
      !selectedOccasion
    ) {
      const categoryWithKlydo = giftCategories?.find((cat) =>
        cat.klydos?.some((k) => k.id === klydoGiftProps.klydoId),
      );
      if (categoryWithKlydo) {
        const foundKlydo = categoryWithKlydo.klydos?.find(
          (k) => k.id === klydoGiftProps.klydoId,
        );
        if (foundKlydo) {
          setSelectedOccasion(categoryWithKlydo);
          setSelectedKlydoGreet(foundKlydo);
        }
      }
    }
  }, [klydoGiftProps?.klydoId, giftCategories, selectedOccasion]);

  const step = search.step;
  const activeStep = steps.findIndex((s) => s.id === step);

  const next = () => {
    setQueryParam('step', steps[activeStep + 1].id, true);
  };

  const canGoBack = history?.state?.idx > 0;
  const back = () => goBack();

  const contextValue = useMemo(
    () => ({
      selectedOccasion,
      selectedKlydoGreet,
      customKlydoId,
      setCustomKlydoId,
      klydoGiftProps,
      setSelectedOccasion,
      next,
      back: canGoBack ? back : undefined,
      setSelectedKlydoGreet,
      setKlydoGiftProps,
      startNewGift,
      resetSenderImageUrl,
      giftCategories,
      admin,
      loading,
      setLoading,
    }),
    [
      selectedOccasion,
      selectedKlydoGreet,
      customKlydoId,
      klydoGiftProps,
      next,
      canGoBack,
      giftCategories,
      admin,
      loading,
    ],
  );

  return (
    <GiftContext.Provider value={contextValue}>{children}</GiftContext.Provider>
  );
};
